import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Zap, Trophy, Flag, Coins, Plus, Calendar, Lock, CheckCircle2, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MyPrizes } from "@/components/MyPrizes";
import { KycPrompt } from "@/components/KycPrompt";

// Some legacy/edge contests were saved without a name - never render a blank title.
function raceTitle(c) {
  const n = (c?.name || "").trim();
  if (n) return n;
  const type = c?.race_type ? c.race_type.charAt(0).toUpperCase() + c.race_type.slice(1) : "Text";
  return `Untitled ${type} Race`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [contests, setContests] = useState([]);
  const [tab, setTab] = useState("registered");
  const [credits, setCredits] = useState(user?.credits ?? 0);

  useEffect(() => {
    api.get("/races/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/contests").then((r) => setContests(r.data.filter((c) => c.status !== "refunded"))).catch(() => {});
    api.get("/credits/balance").then((r) => setCredits(r.data.credits)).catch(() => {});
  }, []);

  const chartData = [...history].reverse().map((h, i) => ({ n: i + 1, cps: h.cps }));
  const winRate = user?.races_played ? Math.round((user.wins / user.races_played) * 100) : 0;

  const goBuyCredits = () => navigate("/buy-credits");

  // A race is "over" once it's completed/cancelled or its start passed the 90-min live window.
  const isOver = (c) =>
    c.status === "completed" || c.status === "cancelled" ||
    new Date(c.start_time).getTime() < Date.now() - 90 * 60 * 1000;

  const mine = contests.filter((c) => c.is_registered);
  const previous = mine.filter(isOver);
  const upcoming = mine.filter((c) => !isOver(c));
  const shown = tab === "registered" ? upcoming : tab === "previous" ? previous : mine;

  const TABS = [
    ["registered", "Registered"],
    ["previous", "Previous Races"],
    ["all", "All"],
  ];

  const emptyMsg = {
    registered: "You have no upcoming registered races.",
    previous: "You haven't completed any races yet.",
    all: "You haven't registered for any races yet.",
  }[tab];

  return (
    <div className="animate-slide-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-race-red">Pit Wall</div>
          <h1 className="font-heading text-5xl tracking-tight">WELCOME, {user?.name?.toUpperCase()}</h1>
        </div>
        {!user?.has_license && (
          <Link to="/license" data-testid="dashboard-claim-license"
            className="flex items-center gap-2 bg-race-volt text-race-bg font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity">
            <Flag size={16} /> Claim your BR License
          </Link>
        )}
      </div>

      <KycPrompt />

      {/* Stats bento */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat Icon={Zap} label="CPS Rating" value={user?.avg_cps ?? user?.skill_cps ?? 0} accent testid="stat-cps" />
        <Stat Icon={Trophy} label="Win Rate" value={`${winRate}%`} testid="stat-winrate" />
        <Stat Icon={Flag} label="Races" value={user?.races_played ?? 0} testid="stat-races" />
        <Stat Icon={Coins} label="Credits" value={credits} testid="stat-credits" action={<button onClick={goBuyCredits} data-testid="buy-credits-btn" aria-label="Buy credits" className="text-race-volt hover:opacity-80"><Plus size={16} /></button>} />
      </div>

      <MyPrizes />

      {/* Chart */}
      <div className="bg-race-surface border border-race-line p-5 mb-8">
        <div className="overline mb-3">CPS Progression</div>
        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData}>
              <XAxis dataKey="n" stroke="#52525b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#52525b" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid #262626", color: "#fff" }} />
              <Line type="monotone" dataKey="cps" stroke="rgb(var(--c-accent))" strokeWidth={2} dot={{ r: 3, fill: "rgb(var(--c-accent))" }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-race-sub text-sm">Run a few races to chart your CPS progression.</p>
        )}
      </div>

      {/* Calendar / contests */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight flex items-center gap-2"><Calendar size={24} className="text-race-red" /> MY RACE CALENDAR</h2>
        <div className="flex items-center gap-3">
          <Link to="/calendar" data-testid="open-full-calendar" className="text-xs font-mono uppercase tracking-wide text-race-sub hover:text-race-txt transition-colors">Full calendar →</Link>
          <div className="flex gap-2">
          {TABS.map(([f, label]) => (
            <button key={f} onClick={() => setTab(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors whitespace-nowrap ${
                tab === f ? "bg-race-red border-race-red text-race-bg" : "border-race-line text-race-sub hover:border-race-txt"
              }`}>{label}</button>
          ))}
        </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((c) => <ContestTile key={c.id} c={c} />)}
        {shown.length === 0 && (
          <div className="col-span-full border border-race-line rounded-md p-8 text-center">
            <p className="text-race-sub mb-3" data-testid="dashboard-empty-msg">{emptyMsg}</p>
            <Link to="/calendar" data-testid="browse-calendar-empty" className="inline-block bg-race-red hover:bg-race-redhover text-race-bg font-heading text-xl tracking-wide px-6 py-2 rounded-sm transition-colors">Browse the calendar</Link>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ Icon, label, value, accent, testid, action }) => (
  <div className="bg-race-surface border border-race-line p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <Icon size={18} className={accent ? "text-race-red" : "text-race-sub"} />
      {action}
    </div>
    <div className={`tabular text-3xl mt-2 ${accent ? "text-race-red" : "text-race-txt"}`}>{value}</div>
    <div className="overline mt-1">{label}</div>
  </div>
);

function ContestTile({ c }) {
  const start = new Date(c.start_time);
  const StatusIcon = c.status === "confirmed" ? CheckCircle2 : Clock;
  const to = c.status === "completed" ? `/results/${c.id}` : `/contests/${c.id}`;
  const title = raceTitle(c);
  return (
    <Link to={to} data-testid={`contest-tile-${c.id}`}
      className="bg-race-surface border border-race-line hover:border-race-red/60 p-4 transition-colors flex flex-col gap-2 group">
      <div className="flex items-center justify-between">
        <span className={`flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide ${c.status === "confirmed" ? "text-race-volt" : "text-race-sub"}`}>
          <StatusIcon size={12} /> {c.status}
        </span>
        <span className="text-[10px] font-mono text-race-muted uppercase">{c.race_type}</span>
      </div>
      <h3 className="font-mono text-lg text-race-txt break-words group-hover:text-race-red transition-colors">{title}</h3>
      {c.sponsor_name && <span className="overline text-race-info">Presented by {c.sponsor_name}</span>}
      <div className="flex items-center justify-between text-xs text-race-sub mt-auto pt-2 border-t border-race-line">
        <span className="tabular">{start.toLocaleDateString()} {start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        {c.status === "completed"
          ? <span className="flex items-center gap-1 text-race-red group-hover:text-race-txt transition-colors"><Trophy size={12} /> Results</span>
          : <span className="flex items-center gap-1">{c.entry_fee_credits > 0 ? <><Coins size={12} /> {c.entry_fee_credits}</> : <span className="text-race-volt">FREE</span>}</span>}
      </div>
    </Link>
  );
}
